import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Video,
  FileText,
  BookOpen
} from "lucide-react";
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LessonPage({ user }) {
  const { lessonId } = useParams();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [prevLesson, setPrevLesson] = useState(null);
  const [nextLesson, setNextLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLesson();
  }, [lessonId]);

  const fetchLesson = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/learning/lessons/${lessonId}`, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setLesson(data.lesson);
        setCourse(data.course);
        setPrevLesson(data.prev_lesson);
        setNextLesson(data.next_lesson);
      } else if (response.status === 404) {
        navigate("/learning");
        toast.error("Lesson not found");
      }
    } catch (error) {
      console.error("Failed to fetch lesson:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async () => {
    setCompleting(true);
    try {
      const response = await fetch(`${API}/learning/lessons/${lessonId}/complete`, {
        method: "POST",
        credentials: "include"
      });
      
      if (response.ok) {
        setLesson(prev => ({ ...prev, completed: true }));
        toast.success("Lesson completed!");
        
        // Auto-navigate to next lesson after a short delay
        if (nextLesson) {
          setTimeout(() => {
            navigate(`/learning/lessons/${nextLesson.lesson_id}`);
          }, 1000);
        }
      } else {
        toast.error("Failed to mark lesson as complete");
      }
    } catch (error) {
      toast.error("Failed to mark lesson as complete");
    } finally {
      setCompleting(false);
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return "";
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours} hours`;
  };

  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Check if URL is a Vimeo video
  const getVimeoId = (url) => {
    if (!url) return null;
    const regExp = /vimeo\.com\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!lesson) {
    return null;
  }

  const youtubeId = getYouTubeId(lesson.video_url);
  const vimeoId = getVimeoId(lesson.video_url);

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in-up" data-testid="lesson-page">
      {/* Back to Course */}
      {course && (
        <Link 
          to={`/learning/courses/${course.course_id}`}
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to {course.title}
        </Link>
      )}

      {/* Lesson Header */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          {lesson.content_type === "video" ? (
            <Video className="w-5 h-5 text-primary" />
          ) : (
            <FileText className="w-5 h-5 text-primary" />
          )}
          <Badge variant="outline">{lesson.content_type}</Badge>
          {lesson.duration_minutes > 0 && (
            <span className="text-sm text-slate-500">
              {formatDuration(lesson.duration_minutes)}
            </span>
          )}
          {lesson.completed && (
            <Badge className="bg-emerald-100 text-emerald-700">
              <CheckCircle className="w-3 h-3 mr-1" />
              Completed
            </Badge>
          )}
        </div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {lesson.title}
        </h1>
        {lesson.description && (
          <p className="text-slate-600 mt-2">{lesson.description}</p>
        )}
      </div>

      {/* Video Content */}
      {lesson.content_type === "video" && lesson.video_url && (
        <Card className="overflow-hidden">
          <div className="aspect-video bg-black">
            {youtubeId ? (
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}`}
                title={lesson.title}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : vimeoId ? (
              <iframe
                src={`https://player.vimeo.com/video/${vimeoId}`}
                title={lesson.title}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video
                src={lesson.video_url}
                controls
                className="w-full h-full"
              >
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        </Card>
      )}

      {/* Text Content */}
      {(lesson.content_type === "text" || lesson.text_content) && lesson.text_content && (
        <Card>
          <CardContent className="prose prose-slate max-w-none p-6">
            <ReactMarkdown>{lesson.text_content}</ReactMarkdown>
          </CardContent>
        </Card>
      )}

      {/* No Content */}
      {!lesson.video_url && !lesson.text_content && (
        <Card>
          <CardContent className="py-12 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No content available for this lesson yet.</p>
          </CardContent>
        </Card>
      )}

      {/* Mark Complete */}
      {!lesson.completed && (
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-6 flex items-center justify-between">
            <div>
              <h3 className="font-medium">Finished this lesson?</h3>
              <p className="text-sm text-slate-500">Mark it complete to track your progress</p>
            </div>
            <Button onClick={handleMarkComplete} disabled={completing}>
              {completing ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Mark Complete
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t">
        {prevLesson ? (
          <Button 
            variant="outline"
            onClick={() => navigate(`/learning/lessons/${prevLesson.lesson_id}`)}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous: {prevLesson.title}
          </Button>
        ) : (
          <div />
        )}
        
        {nextLesson ? (
          <Button onClick={() => navigate(`/learning/lessons/${nextLesson.lesson_id}`)}>
            Next: {nextLesson.title}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : course ? (
          <Button onClick={() => navigate(`/learning/courses/${course.course_id}`)}>
            Back to Course
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        ) : null}
      </div>
    </div>
  );
}
